/**
 * Page transitions — types + framer-motion revival helpers.
 *
 * Source of truth for transition data is the backend
 * (`GET /api/transitions`, `GET /api/admin/transitions`). This file
 * only owns:
 *  - the `ProposalTransition` shape (mirrors the Prisma model)
 *  - the `framerVariants` JSON envelope used to round-trip direction-
 *    aware `Variants` through the API
 *
 * Both apps pull their catalogue over the wire; nothing here is
 * authoritative and nothing here needs to be kept in sync with seed
 * changes.
 */
import type { Variants, Variant } from 'framer-motion';

export type TransitionTier = 'FREE' | 'PRO';
export type TransitionKind = 'fm' | 'css';
export type TransitionSource = 'folder-1' | 'folder-2';

export interface ProposalTransition {
  id: string;
  name: string;
  description: string | null;
  emoji: string | null;
  source: TransitionSource;
  kind: TransitionKind;
  tier: TransitionTier;
  isEnabled: boolean;
  cssDataT: string | null;
  cssDurationMs: number;
  framerVariants: SerializedVariants | null;
  sortOrder: number;
}

export interface SerializedFwdBwd {
  __fn: 'fwdBwd';
  fwd: Record<string, unknown>;
  bwd: Record<string, unknown>;
}

export type SerializedVariantEntry = Variant | SerializedFwdBwd;

export interface SerializedVariants {
  initial: SerializedVariantEntry;
  animate: Variant;
  exit: SerializedVariantEntry;
}

export function reviveFramerVariants(s: SerializedVariants): Variants {
  return {
    initial: reviveEntry(s.initial),
    animate: s.animate,
    exit: reviveEntry(s.exit),
  };
}

function reviveEntry(e: SerializedVariantEntry): Variants['initial'] {
  if (e && typeof e === 'object' && '__fn' in e && e.__fn === 'fwdBwd') {
    const { fwd, bwd } = e;
    return ((direction: number) => (direction > 0 ? fwd : bwd)) as Variants['initial'];
  }
  return e as Variants['initial'];
}
